import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PermisoEnModulo } from '../../api-client/permisos/modulo';
import config from '../../config';
import type { Envelope } from '../../utils/envelope';
import { useUIStore } from '../ui/ui.store';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: SystemUser | null;
  expirationTime: number | null;
  renewTimeFrame: number;
  renewDialogVisible: boolean;
  permisos: PermisoEnModulo[];

  login: (username: string, password: string) => Promise<void>;
  renewToken: (refreshToken: string) => Promise<void>;
  logout: () => void;
  showRenewDialog: () => void;
  hideRenewDialog: () => void;
  updateTimeFrame: (newTimeFrame: number) => void;
}

export type SystemUser = {
  id: string;
  nombre: string;
  refreshToken: string;
  exp: number; // Expiration
};

const { showErrorToast, showLoading, hideLoading } = useUIStore.getState();

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      token: null,
      user: null,
      expirationTime: null,
      renewTimeFrame: 90 * 1000, // 90 seconds
      renewDialogVisible: false,
      permisos: [],

      login: async (username: string, password: string) => {
        showLoading();

        const data = await fetchToken(username, password);

        hideLoading();

        if (data.isFailure) {
          showErrorToast(data.errorMessage);
          return;
        }

        const user = parseUserToken(data.result);

        const responsePermisos = await fetchUserPermissions(data.result, user.id);

        set({
          isAuthenticated: true,
          token: data.result,
          user,
          expirationTime: user.exp * 1000,
          renewTimeFrame: 90 * 1000,
          renewDialogVisible: false,
          permisos: responsePermisos.result
        });
      },
      renewToken: async (refreshToken: string) => {
        showLoading();

        const currentState = get();
        const data = await renewToken(currentState.token!, refreshToken);

        hideLoading();

        if (data.isFailure) {
          showErrorToast(data.errorMessage);
          return;
        }

        const user = parseUserToken(data.result);

        set({
          isAuthenticated: true,
          token: data.result,
          user,
          expirationTime: user.exp * 1000,
          renewDialogVisible: false,
          renewTimeFrame: 90 * 1000
        });
      },
      updateTimeFrame: (newTimeFrame: number) =>
        set({
          renewTimeFrame: newTimeFrame
        }),
      showRenewDialog: () => set({ renewDialogVisible: true }),
      hideRenewDialog: () => set({ renewDialogVisible: false }),
      logout: () =>
        set({
          isAuthenticated: false,
          token: null,
          user: null,
          expirationTime: null,
          renewDialogVisible: false,
          renewTimeFrame: 90 * 1000
        })
    }),
    {
      name: 'auth'
    }
  )
);

const parseUserToken = (token: string): SystemUser => {
  const [, payload] = token.split('.');
  const base64 = payload.replace('-', '+').replace('_', '/');

  return JSON.parse(atob(base64));
};

const fetchToken = async (username: string, password: string) => {
  let data: Envelope<string> = {} as Envelope<string>;

  try {
    const response = await fetch(`${config.apiUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        idUsuario: username,
        password
      })
    });

    data = await response.json();
  } catch (ex) {
    data = {
      isFailure: true,
      isSuccess: false,
      errorMessage: 'Uexpected error',
      result: ''
    };
  }

  return data;
};

const fetchUserPermissions = async (token: string, userId: string) => {
  let data: Envelope<PermisoEnModulo[]> = {} as Envelope<PermisoEnModulo[]>;
  try {
    const response = await fetch(`${config.apiUrl}/permisos/usuario/${userId}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        authorization: `bearer ${token}`
      }
    });

    data = await response.json();
  } catch (ex) {
    data = {
      isFailure: true,
      isSuccess: false,
      errorMessage: 'Unexpected error',
      result: []
    };
  }

  return data;
};

const renewToken = async (token: string, refreshToken: string) => {
  let data: Envelope<string> = {} as Envelope<string>;
  try {
    const response = await fetch(`${config.apiUrl}/auth/renew`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `bearer ${token}`
      },
      body: JSON.stringify({
        refreshToken
      })
    });

    data = await response.json();
  } catch (ex) {
    data = {
      isFailure: true,
      isSuccess: false,
      errorMessage: 'Unexpected error',
      result: ''
    };
  }

  return data;
};
